import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { shallow } from 'zustand/shallow';
import {
  Button,
  Container,
  ContainerCard,
  FindingPricesLoader,
  Icon,
  InfoLabel,
  PopoverWithMouthOver,
  Popup,
  Steps,
} from '@/components/common';
import { useBuyCrypto } from '@/store/buyCrypto';
import { useCancelBuyTxn, useGetBuyTxn } from '@/api/queryHooks/useBuyCryptoController';
import { useCreateNewBuyTx } from '@/views/BuyCrypto/components/WaitingPaymentLoader/useCreateNewBuyTx';
import { mapCryptoFrontendStatusToPaymentStatusType, mapTxTypeDtoToTxType } from '@/mappers';
import { useUpdateRate } from '@/hooks/useUpdateRate';
import { HighDemand, VerificationFailed } from '@/components/common/popups';
import { useVerifyStore } from '@/store/verify';
import { classifyBuyTxnError, getBuyTxnErrorCode } from '@/utils/buyTxnError';
import { getKycErrorMessage } from '@/constants/kycErrorCodes';
import { OverlayProvider } from 'react-aria';
import { useOverlayTriggerState } from 'react-stately';
import { showNotification } from '@/utils/showNotification';
import { PaymentStatusType } from '@/constants/paymentStatuses';
import { useRole } from '@/hooks/useRole';
import { Routes } from '@/constants/routes';
import { useWindowSize } from 'usehooks-ts';
import WalletConnectProvider from '@/utils/walletConnectProvider';
import { NewBankDetails } from '@/components/common/popups/NewBankDetails/NewBankDetails';
import useNewBankDetailsModal from '@/hooks/useNewBankDetailsModal';
import { useRouter } from 'next/router';
import { BuyCheckoutData } from './components';
import { EnterAmount, Payment, WalletAddress } from './BuyCryptoSteps';
import styles from './BuyCryptoPage.module.css';

const STEPS = ['Enter Amount', 'Payment', 'Wallet', 'Checkout'];

export const BuyCryptoPage = () => {
  const [step, setStep] = useState(1);
  const buyState = useBuyCrypto((state) => state, shallow);
  const { createNewTx, txData, createNewTxError, isCreateNewTxLoading } = useCreateNewBuyTx();
  const setCheckId = useVerifyStore((state) => state.setCheckId);
  const updateRate = useUpdateRate({
    symbol: buyState.get,
    isBuy: true,
    isFetchOnMount: false,
    network: buyState?.network,
    isPrivateCall: true,
  });
  const { mutateAsync: cancelBuyTxn, isLoading: isCancelBuyTxnLoading } = useCancelBuyTxn();
  const role = useRole();
  const { width } = useWindowSize();
  const popoverState = useOverlayTriggerState({});
  const { handleModalClose, isModalShown } = useNewBankDetailsModal();
  const router = useRouter();

  useGetBuyTxn([
    { id: txData?.id },
    {
      enabled: false,
      onSuccess(data) {
        buyState.setData({ txData: data });
      },
    },
  ]);
  const status = mapCryptoFrontendStatusToPaymentStatusType(buyState.txData?.frontendStatus);

  function startNewPurchase() {
    setStep(1);
  }

  const renderStep = (step: number) => {
    switch (step) {
      case 2:
        return <Payment step={step} setStep={setStep} />;
      case 3:
        return <WalletAddress step={step} setStep={setStep} />;
      case 4:
        return (
          <BuyCheckoutData
            status={status}
            retryTx={retryTx}
            cancelTxn={cancelTxn}
            isCancelBuyTxnLoading={isCancelBuyTxnLoading}
          />
        );
      default:
        return <EnterAmount step={step} setStep={setStep} />;
    }
  };

  function retryTx() {
    createNewTx();
    updateRate();
  }

  function onHighDemandReturn() {
    highDemandState.close();
    setStep(1);
    buyState.setIsFindingBestPrices(false);
  }

  function closePopover() {
    popoverState.close();
    localStorage.setItem('scamsPopupClosed', 'true');
  }

  async function cancelTxn() {
    if (buyState.txData?.id) {
      try {
        await cancelBuyTxn(buyState.txData.id);
        setStep(1);
        showNotification('Transaction canceled', 'success');
      } catch (e) {
        showNotification();
      }
    }
  }

  useEffect(() => {
    if (step !== 4) return;
    createNewTx();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);
  const highDemandState = useOverlayTriggerState({});
  const verificationFailedState = useOverlayTriggerState({});

  // The create-buy-transaction call can fail for very different reasons. Branch on the
  // backend error code instead of always blaming "high demand": a KYC/biometric failure
  // should send the user back to (re)verify, exhausted attempts go to the support modal,
  // and only genuinely transient errors keep the high-demand popup.
  // Keyed on the error object (not `step`) so a fresh failure produces a fresh reaction and a
  // stale error doesn't bounce the user when they re-enter checkout after re-verifying.
  useEffect(() => {
    if (!createNewTxError) return;
    const kind = classifyBuyTxnError(createNewTxError);
    if (kind === 'reverify') {
      setCheckId('');
      buyState.setIsFindingBestPrices(false);
      setStep(3);
      showNotification(getKycErrorMessage(getBuyTxnErrorCode(createNewTxError)));
    } else if (kind === 'attemptsExhausted') {
      verificationFailedState.open();
    } else {
      highDemandState.open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createNewTxError]);

  useEffect(() => {
    // Syncs the step state with the URL query parameter "step" (for analytics/tracking purposes)
    if (!router.isReady) return;

    const stepNames: Record<number, string> = {
      2: 'payment',
      3: 'wallet',
      4: 'checkout',
    };

    const currentQuery = { ...router.query };
    const stepName = stepNames[step];

    if (stepName) {
      if (currentQuery.step !== stepName) {
        router.replace(
          {
            pathname: router.pathname,
            query: { ...currentQuery, step: stepName },
          },
          undefined,
          { shallow: true },
        );
      }
    } else if (currentQuery.step) {
      delete currentQuery.step;
      router.replace(
        {
          pathname: router.pathname,
          query: currentQuery,
        },
        undefined,
        { shallow: true },
      );
    }
  }, [step, router.isReady, router]);

  useEffect(() => {
    // Sync the status of the transaction with the URL query parameter "status" (for analytics/tracking purposes)
    if (!router.isReady) return;

    const currentQuery = { ...router.query };

    if (status) {
      if (status === PaymentStatusType.Completed && step === 4) {
        if (currentQuery.status !== PaymentStatusType.Completed) {
          router.replace(
            {
              pathname: router.pathname,
              query: { ...currentQuery, status: PaymentStatusType.Completed },
            },
            undefined,
            { shallow: true },
          );
        }
      } else if (currentQuery.status) {
        delete currentQuery.status;
        router.replace(
          {
            pathname: router.pathname,
            query: currentQuery,
          },
          undefined,
          { shallow: true },
        );
      }
    }
  }, [router, status, step]);

  const popover = (
    <div className={styles.popover}>
      <PopoverWithMouthOver triggerSrc="/assets/questionMark.svg" state={popoverState} isHoverActive={false}>
        <div className={styles.wrapper}>
          <div className={styles.popoverCloseIcon} onClick={closePopover}>
            <Icon name="close" />
          </div>
          <div className={styles.popoverText}>
            <span className={styles.popoverTextBold}>Note:</span> RelayPay will never send you an SMS or email that asks
            for your password, to share your crypto wallet key, or include a link to login directly from SMS or email.
            <p className={styles.popoverLinkText}>
              If you receive a call from someone claiming to be from RelayPay and you are unsure, do not give out any
              details. Read our guide for more:{' '}
              <a
                className={styles.popoverLink}
                target="_blank"
                rel="noreferrer"
                href="https://www.relaypay.io/post/crypto-investment-scams-australia-how-to-avoid"
              >
                Most common scams (and tips to avoid them).
              </a>
            </p>
          </div>
        </div>
      </PopoverWithMouthOver>
    </div>
  );

  useEffect(() => {
    const scamPopupClosed = localStorage.getItem('scamsPopupClosed');

    if (!scamPopupClosed) {
      popoverState.open();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <WalletConnectProvider>
      <Container className={styles.container}>
        <div className={styles.mainInner}>
          <Steps active={step} options={STEPS} className={styles.steps} />

          {width >= 1000 && popover}

          {buyState.isFindingBestPrices ? (
            <FindingPricesLoader />
          ) : (
            <>
              {step === 4 && (
                <>
                  {(status === PaymentStatusType.Completed || status === PaymentStatusType.Processing) && (
                    <h3 className={styles.checkoutTitleMobile}>Checkout</h3>
                  )}
                  <div
                    className={clsx(styles.checkoutTitleWrapper, {
                      [styles.checkoutTitleWrapperMobile]:
                        status === PaymentStatusType.Completed || status === PaymentStatusType.Processing,
                    })}
                  >
                    {(status === PaymentStatusType.Completed || status === PaymentStatusType.Processing) &&
                    width < 1000 ? null : (
                      <h3 className={styles.checkoutTitle}>Checkout</h3>
                    )}

                    {(status === PaymentStatusType.Completed || status === PaymentStatusType.Processing) && (
                      <div className={styles.buttons}>
                        <Button variants="subtle" size="mediumSubtle" onPress={startNewPurchase}>
                          Start New Purchase
                        </Button>
                        <Button href={`/${role}${Routes.TRANSACTIONS}`}>See All Transactions</Button>
                      </div>
                    )}
                  </div>
                </>
              )}
              <ContainerCard
                width={step === 4 ? 1246 : 480}
                className={clsx(styles.containerCard, {
                  [styles.checkoutContainerCard]: step === 4,
                })}
              >
                {renderStep(step)}
              </ContainerCard>
            </>
          )}
        </div>
        {step === 4 ? (
          <OverlayProvider>
            <Popup isOpen={highDemandState.isOpen} onClose={highDemandState.close} hasCloseIcon isDismissable={false}>
              <HighDemand
                onRefresh={createNewTx}
                onReturn={onHighDemandReturn}
                isLoadingRefresh={isCreateNewTxLoading}
              />
            </Popup>
            <Popup
              isOpen={verificationFailedState.isOpen}
              onClose={verificationFailedState.close}
              hasCloseIcon
              isDismissable={false}
            >
              <VerificationFailed status="multipleVerification" />
            </Popup>
          </OverlayProvider>
        ) : (
          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <InfoLabel icon="australia" size={24} className={styles.infoLabel}>
              Built with love in Australia
            </InfoLabel>
            {width < 1000 && popover}
          </div>
        )}

        <OverlayProvider>
          <Popup isOpen={isModalShown}>
            <NewBankDetails onClose={handleModalClose} />
          </Popup>
        </OverlayProvider>
      </Container>
    </WalletConnectProvider>
  );
};
